/*
 * Wall Set picker + Add-to-cart wiring.
 *
 * Boots inside <wall-set-picker data-wsp-root> and:
 *  1. Reads the embedded `recipe` + price-map JSON. The price map is
 *     either the single-piece map (default) or the One Piece Set map,
 *     selected by the optional `data-price-map-source` attribute on the
 *     root ("single" | "set"; absent ⇒ "single"). Same JSON shape either
 *     way; the flag is forwarded in the emitted event so the ATC layer
 *     can add ONE set line item vs. the per-print bulk payload.
 *  2. Computes available formats (intersection of price-map keys against
 *     {art_print, framed_art_print, canvas_print, framed_canvas_print}).
 *  3. Renders format tiles, then size pills (constant-size only — mixed-
 *     size Sets use each print's authored_size), then frame swatches when
 *     the selected format is framed.
 *  4. Defaults to the Set's authored_format / authored_frame_color / size
 *     so the page loads in a sensible state.
 *  5. Emits `wallset:selection:changed` events on every selection. The
 *     wall-set-price-atc component listens for these, recomputes the
 *     total, and unlocks the Add-to-cart button.
 *  6. On Add-to-cart, iterates `recipe.prints`, looks up each
 *     `format|size|frame` → variant_id in the price map, and POSTs a
 *     single bulk payload to /cart/add.js with one line per print.
 *     Properties: _design_id + _set_id + _set_position so the order
 *     webhook decodes correctly.
 *
 * No bundler — vanilla browser ES2017+. Loaded by main-product.liquid
 * with `defer` so the DOM is already there on execute.
 */
(function () {
  'use strict';

  const FORMAT_LABELS = {
    art_print: 'Art Print',
    framed_art_print: 'Framed Art Print',
    canvas_print: 'Canvas',
    framed_canvas_print: 'Framed Canvas',
  };
  const FRAME_LABELS = {
    none: 'Unframed',
    black: 'Black',
    white: 'White',
    natural_oak: 'Natural Oak',
  };
  const FRAMED_FORMATS = ['framed_art_print', 'framed_canvas_print'];

  function bootAllPickers() {
    document.querySelectorAll('wall-set-picker[data-wsp-root]').forEach(bootPicker);
  }

  function bootPicker(root) {
    if (root.__wspBooted) return;
    root.__wspBooted = true;

    // ── ONE_PIECE_SET: price-map source parameterization ──────────────
    // The picker reads the price map from the embedded
    // `[data-wsp-pricing-map]` <script> tag (populated by the Liquid
    // snippet). `data-price-map-source` is an OPTIONAL label declaring
    // WHICH shop metafield the embedded JSON came from:
    //   - absent / "single" / "wall_set"  → single-piece map
    //     (shop.metafields.wall_art_config.pricing_variant_map) — the
    //     existing wall-set + single-product behavior, UNCHANGED.
    //   - "set" → set price map
    //     (shop.metafields.wall_art_config.set_pricing_variant_map),
    //     used by One Piece Set products so the picker shows set prices.
    // The JSON shape ("format|size|frame_color" → {variant_id, price})
    // is IDENTICAL for both maps, so availability/emit logic is the same;
    // this flag only flows into the emitted event detail so the ATC
    // component can branch (single line vs. per-print bulk add). Defaults
    // keep wall-set/single behavior byte-for-byte the same.
    const priceMapSource = (root.dataset.priceMapSource || 'single').trim() || 'single';

    let priceMap, recipe;
    try {
      priceMap = parseMaybeDouble(root.querySelector('[data-wsp-pricing-map]').textContent);
      recipe = parseMaybeDouble(root.querySelector('[data-wsp-recipe]').textContent);
    } catch (err) {
      console.error('[wall-set-picker] failed to parse embedded JSON', err);
      return;
    }
    if (!recipe || !Array.isArray(recipe.prints) || recipe.prints.length === 0) {
      console.warn('[wall-set-picker] recipe has no prints; recipe=', recipe);
      return;
    }
    console.info('[wall-set-picker] booted', {
      set_id: root.dataset.setId,
      price_map_source: priceMapSource, // ONE_PIECE_SET: 'single' | 'set'
      prints: recipe.prints.length,
      is_constant_size: recipe.is_constant_size,
      authored_format: recipe.authored_format,
      authored_size: recipe.authored_size,
      authored_frame_color: recipe.authored_frame_color,
      price_map_keys: Object.keys(priceMap || {}).length,
      sample_price_map_keys: Object.keys(priceMap || {}).slice(0, 4),
    });

    // Possible (format, size, frame_color) combos from the price map.
    // Use a Map so the picker only shows formats/sizes/frames that actually
    // resolve to a real variant.
    const availability = buildAvailability(priceMap);

    // Read any shareable selection from the URL (?ws_format=...&ws_size=...&ws_frame=...).
    // These take precedence over the Set's authored defaults so a copied
    // link opens at the same variant the sender was looking at.
    const urlSel = readSelectionFromUrl();

    // Selection state. Defaults to URL → authored → first available.
    const state = {
      format: (urlSel.format && availability.formats.has(urlSel.format))
        ? urlSel.format
        : (recipe.authored_format && availability.formats.has(recipe.authored_format))
          ? recipe.authored_format
          : Array.from(availability.formats)[0],
      size: urlSel.size
        ? urlSel.size
        : (recipe.is_constant_size && recipe.authored_size)
          ? recipe.authored_size
          : null,
      frame_color: (urlSel.frame && urlSel.frame !== 'none')
        ? urlSel.frame
        : (recipe.authored_frame_color && recipe.authored_frame_color !== 'none')
          ? recipe.authored_frame_color
          : 'black',
    };

    const formatGrid = root.querySelector('[data-wsp-format-grid]');
    const formatLabel = root.querySelector('[data-wsp-format-selected-label]');
    const sizePicker = root.querySelector('[data-wsp-size-picker]');
    const sizePills = root.querySelector('[data-wsp-size-pills]');
    const sizeLabel = root.querySelector('[data-wsp-size-selected-label]');
    const framePicker = root.querySelector('[data-wsp-frame-picker]');
    const frameSwatches = root.querySelector('[data-wsp-frame-swatches]');
    const frameLabel = root.querySelector('[data-wsp-frame-selected-label]');

    function isFramed(format) { return FRAMED_FORMATS.includes(format); }

    function emit() {
      const frameKey = isFramed(state.format) ? state.frame_color : 'none';
      // Hero metafield keys use 'unframed' for unframed prints; the picker
      // uses 'none'. Re-map for the design-side hero lookup.
      const heroFrameKey = frameKey === 'none' ? 'unframed' : frameKey;
      const lines = recipe.prints.map((print, idx) => {
        const size = recipe.is_constant_size ? state.size : print.size;
        const key = `${state.format}|${size}|${frameKey}`;
        const entry = priceMap[key];
        if (!entry) {
          console.warn(`[wall-set-picker] no price-map entry for key "${key}" (plane ${print.plane_number ?? idx + 1})`);
        }
        // Pick the per-print hero for the current format×frame_color so the
        // cart line item shows the framed/canvas mockup instead of the raw
        // 2x3 artwork. Falls back to the raw URL when the design hasn't been
        // bulk-rendered yet (older catalog entries).
        const heroVariantKey = `${state.format}_${heroFrameKey}`;
        const designImage = (print.hero_images_by_variant && print.hero_images_by_variant[heroVariantKey])
          || print.artwork_image;
        return {
          plane_number: print.plane_number ?? (idx + 1),
          design_id: print.artwork_id,
          design_handle: print.artwork_handle,
          design_name: print.artwork_name,
          // `design_image` is the per-format/per-frame_color HERO (with
          // frame baked in) — used by the cart display so customers see
          // the framed mockup instead of raw artwork.
          design_image: designImage,
          // `artwork_url` is the raw 2x3 artwork (no frame/canvas baked
          // in) — used by the storefront gallery compositor for slots
          // 2-4 so the compositor draws the artwork flat onto the scene
          // and applies its own shadow / framing / canvas treatment.
          artwork_url: print.artwork_image,
          size: size,
          format: state.format,
          frame_color: frameKey,
          lookup_key: key,
          variant_id: entry ? entry.variant_id : null,
          price: entry ? Number(entry.price) : null,
        };
      });
      const resolvable = lines.every((l) => l.variant_id != null);
      // Swap the gallery's slot-1 image to the rendered Set hero for the
      // current (format, frame_color). Recipe metafield carries the 8 URLs
      // keyed `<format>_<frame_color>` — the hero compositor uses
      // `unframed` (not `none`) as the unframed key, so we re-map here.
      swapHeroImage(state.format, frameKey);
      // Mirror the selection into the URL so a copied link opens at the
      // same variant. Uses `ws_*` prefix to avoid colliding with the
      // single-product picker's `wa_*` params.
      writeSelectionToUrl(state);
      // ONE_PIECE_SET: surface the price-map source + set_id so the ATC
      // component knows whether to add ONE set line item (source==='set')
      // or the per-print bulk payload (default wall-set behavior). The
      // existing wall-set ATC ignores these extra keys, so wall-set pages
      // are unaffected.
      const detail = {
        state: { ...state },
        lines,
        resolvable,
        recipe,
        priceMapSource,
        setId: root.dataset.setId || '',
      };
      root.dispatchEvent(new CustomEvent('wallset:selection:changed', {
        bubbles: true,
        detail,
      }));
      // Bridge to the wall-art picker's protocol so single-product scripts
      // loaded on set pages (featured section, hero swap, etc.) react to
      // our format/frame_color changes:
      //   1. Set window.__wallArtCurrentSelection — what product-type-features.js
      //      reads in resolveSelectedVariant() to pick the active format.
      //      Without this it sees no selection and falls back to the
      //      product's `Wall Art Set` type, which matches no format.
      //   2. Dispatch wallart:selection:changed — the event listener those
      //      scripts use to schedule their next render.
      if (typeof window !== 'undefined') {
        window.__wallArtCurrentSelection = {
          format: state.format,
          size: state.size,
          frameColor: frameKey,
        };
      }
      // `bubbles: true` is REQUIRED: the product-specs format switcher
      // (main-product.liquid) and the dynamic-size script
      // (product-specs-dynamic-size.js) both listen on `window`. An event
      // dispatched on `document` only reaches window listeners when it
      // bubbles (document → window). Without this the Set page's spec
      // block never switches format (stuck on the `default` fallback) and
      // the Artwork/Finished size rows never populate. Matches the
      // single-product picker, which dispatches with bubbles:true too.
      document.dispatchEvent(new CustomEvent('wallart:selection:changed', {
        bubbles: true,
        detail: {
          format: state.format,
          size: state.size,
          frameColor: frameKey,
        },
      }));
    }

    function swapHeroImage(format, frameKey) {
      if (!recipe || !recipe.hero_images) return;
      // Hero keys use 'unframed' for unframed; picker emits 'none'. Re-map.
      const heroFrameKey = frameKey === 'none' ? 'unframed' : frameKey;
      const url = recipe.hero_images[`${format}_${heroFrameKey}`];
      if (!url) return;
      // Active slide first (Dawn's product gallery pattern). Fall back to
      // any product__media-item <img> if the gallery markup differs.
      let img = document.querySelector(
        '.product__media-item.is-active img, ' +
        'media-gallery .product__media-item.is-active img',
      );
      if (!img) {
        img = document.querySelector(
          '.product__media-item img, media-gallery .product__media-item img',
        );
      }
      if (!img) return;
      img.setAttribute('src', url);
      if (img.hasAttribute('srcset')) img.removeAttribute('srcset');
      img.setAttribute('alt', `${recipe.set_id || ''} — ${humanizeFormatLocal(format)}`);
      // Drop any <source> in a wrapping <picture> so the browser doesn't
      // pick a stale responsive variant.
      const picture = img.closest && img.closest('picture');
      if (picture) {
        for (const s of picture.querySelectorAll('source')) s.remove();
      }
    }

    function humanizeFormatLocal(f) {
      return FORMAT_LABELS[f] || f;
    }

    function renderFormats() {
      // Liquid pre-rendered the 4 format tiles with icons + labels. We
      // just toggle the `--selected` class + aria-checked, and hide
      // tiles whose format isn't in the price map.
      const tiles = formatGrid.querySelectorAll('[data-wsp-format]');
      tiles.forEach((tile) => {
        const fmt = tile.getAttribute('data-wsp-format');
        if (!availability.formats.has(fmt)) {
          tile.style.display = 'none';
          return;
        }
        tile.style.display = '';
        const isSelected = state.format === fmt;
        tile.classList.toggle('product-type-switcher__item--active', isSelected);
        tile.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        if (!tile.__wspBound) {
          tile.__wspBound = true;
          tile.addEventListener('click', () => {
            state.format = fmt;
            if (recipe.is_constant_size) {
              const sizesForFormat = availability.byFormat.get(fmt)?.sizes;
              if (sizesForFormat && !sizesForFormat.has(state.size)) {
                state.size = Array.from(sizesForFormat)[0] ?? null;
              } else if (!state.size && sizesForFormat) {
                state.size = Array.from(sizesForFormat)[0] ?? null;
              }
            }
            renderAll();
            emit();
          });
        }
      });
    }

    function renderSizes() {
      if (!recipe.is_constant_size) {
        sizePicker.hidden = true;
        return;
      }
      sizePicker.hidden = false;
      sizePills.innerHTML = '';
      const sizes = Array.from(availability.byFormat.get(state.format)?.sizes ?? []);
      if (!state.size && sizes[0]) state.size = sizes[0];
      for (const s of sizes) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'wap-size-pill' + (state.size === s ? ' wap-size-pill--selected' : '');
        btn.setAttribute('role', 'radio');
        btn.setAttribute('aria-checked', state.size === s ? 'true' : 'false');
        btn.textContent = s;
        btn.addEventListener('click', () => {
          state.size = s;
          renderAll();
          emit();
        });
        sizePills.appendChild(btn);
      }
      sizeLabel.textContent = state.size || '';
    }

    function renderFrames() {
      if (!isFramed(state.format)) {
        framePicker.hidden = true;
        return;
      }
      framePicker.hidden = false;
      frameSwatches.innerHTML = '';
      const frames = Array.from(availability.byFormat.get(state.format)?.frames ?? []);
      const usable = frames.filter((f) => f !== 'none');
      if (!usable.includes(state.frame_color) && usable[0]) state.frame_color = usable[0];
      for (const f of usable) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'wap-swatch' + (state.frame_color === f ? ' wap-swatch--selected' : '');
        btn.setAttribute('role', 'radio');
        btn.setAttribute('aria-checked', state.frame_color === f ? 'true' : 'false');
        btn.title = FRAME_LABELS[f] || f;
        const swatch = document.createElement('span');
        swatch.className = `wap-swatch__color wap-swatch__color--${f}`;
        btn.appendChild(swatch);
        btn.addEventListener('click', () => {
          state.frame_color = f;
          renderAll();
          emit();
        });
        frameSwatches.appendChild(btn);
      }
      frameLabel.textContent = FRAME_LABELS[state.frame_color] || '';
    }

    function renderAll() {
      renderFormats();
      renderSizes();
      renderFrames();
    }

    renderAll();
    // Defer first emit so the price-atc element (further down the DOM)
    // has time to register its own boot listener.
    requestAnimationFrame(emit);
  }

  /** JSON metafields written without a definition come through as
   *  strings; with a definition they come through as objects. Parse
   *  defensively — try once, and if the result is still a string, parse
   *  again. (Liquid's `| json` filter wraps a string in another layer of
   *  quotes, so we get `"\"actual json...\""`.) */
  function parseMaybeDouble(text) {
    if (text == null) return null;
    const stripped = String(text).trim();
    if (!stripped) return null;
    let parsed = JSON.parse(stripped);
    if (typeof parsed === 'string') {
      try { parsed = JSON.parse(parsed); } catch { /* leave as-is */ }
    }
    return parsed;
  }

  /** Read the variant selection from the URL query string. Returns
   *  `{ format, size, frame }` with each field a non-empty trimmed
   *  string OR undefined. Boot picks these over the recipe's authored
   *  defaults, so copying a Set product URL preserves the visible
   *  variant. */
  function readSelectionFromUrl() {
    try {
      const u = new URL(window.location.href);
      const get = (k) => {
        const v = u.searchParams.get(k);
        return v && v.trim() ? v.trim() : undefined;
      };
      return {
        format: get('ws_format'),
        size:   get('ws_size'),
        frame:  get('ws_frame'),
      };
    } catch { return {}; }
  }

  /** Mirror selection into the URL via `history.replaceState` (no new
   *  history entry — back button still exits to the previous page).
   *  Strips Shopify's native `?variant=` since the Set product has
   *  only the placeholder variant. */
  function writeSelectionToUrl(state) {
    if (!state.format || !state.size) return;
    try {
      const u = new URL(window.location.href);
      u.searchParams.set('ws_format', state.format);
      u.searchParams.set('ws_size', state.size);
      if (state.frame_color && state.frame_color !== 'none') {
        u.searchParams.set('ws_frame', state.frame_color);
      } else {
        u.searchParams.delete('ws_frame');
      }
      u.searchParams.delete('variant');
      history.replaceState(null, '', u.toString());
    } catch {
      // ignore — URL update is non-critical
    }
  }

  function buildAvailability(priceMap) {
    const formats = new Set();
    const byFormat = new Map();    // format → { sizes: Set, frames: Set }
    for (const key of Object.keys(priceMap)) {
      const parts = key.split('|');
      if (parts.length !== 3) continue;
      const [format, size, frame] = parts;
      formats.add(format);
      if (!byFormat.has(format)) byFormat.set(format, { sizes: new Set(), frames: new Set() });
      const entry = byFormat.get(format);
      entry.sizes.add(size);
      entry.frames.add(frame);
    }
    return { formats, byFormat };
  }

  // The price-atc element has its own inline boot script (defined in
  // wall-set-price-atc.liquid) — listens for `wallset:selection:changed`,
  // updates the total + sticky mirror, owns the ATC click handler. That
  // section-scoped logic supersedes the old picker-side bootPriceAtc, so
  // there's nothing left to wire from here.

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      bootAllPickers();
    });
  } else {
    bootAllPickers();
  }
})();
